import {LinkAccountStore} from "./store";
import {AuthProviderType} from "../../domain/providers";
import {useAuth} from "../auth/AuthContext";
import {linkAccount} from "../../firebase";
import {OAuthCredential} from "@firebase/auth";

export type LoadingReturn = {
  status: "loading"
}

export type RedirectReturn = {
  status: "redirect";
  path: string;
}

export type LinkSignIn = {
  status: "sign-in";
  email: string;
  linkingWith: AuthProviderType;
}

export type LinkAccount = {
  status: "link";
  linkAccounts: () => Promise<void>;
}

export type UseMergeAccountReturn = RedirectReturn | LinkSignIn | LoadingReturn | LinkAccount;

export const useMergeAccount = (): UseMergeAccountReturn => {
  const {email, identityProvider, credentialJSON} = LinkAccountStore.get();
  if (!email) {
    return {status: "redirect", path: "/signup"};
  }

  const {user, loading: isUserLoading, error: isUserError} = useAuth();
  if ( (isUserError && isUserError.code !== "auth/account-exists-with-different-credential")) {
    return {status: "redirect", path: "/error"};
  }

  if (isUserLoading) {
    return {status: "loading"};
  }

  if (user) {
    const linkAccounts = async () => {
      const credential = OAuthCredential.fromJSON(JSON.parse(credentialJSON || ""))
      if (!credential) {
        throw `no credentials for ${identityProvider}`
      }

      await linkAccount(credential)
      .catch(err => {
        if (err.code === "auth/provider-already-linked") {
          return
        }
        throw err;
      });
    };
    return {
      status: "link",
      linkAccounts,
    }
  }
  if (email && identityProvider) {
    return {
      status: "sign-in",
      email,
      linkingWith: identityProvider,
    };
  }

  return {
    status: "redirect",
    path: "/signup"
  }
};

export const beginMergeAccount = (email: string, identityProvider: AuthProviderType, credential: OAuthCredential) => {
  if (!email) {
    throw new Error("Email is required to link account");
  }
  if (!identityProvider) {
    throw new Error("Identity Provider is required to link account");
  }
  if (!credential) {
    throw new Error("Oauth credential is required to link account");
  }
  LinkAccountStore.set({email, identityProvider, credentialJSON: JSON.stringify(credential.toJSON())});
};

export type MergeAccountInfo = {
  email?: string
  identityProvider?: AuthProviderType
  credentialJSON?: string
}

export const getMergeAccount = (): MergeAccountInfo => {
  return LinkAccountStore.get();
};

export const clearMergeAccount = () => LinkAccountStore.clear();
